import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from 'antd';
import { useDispatch } from 'dva';
import _ from 'lodash';
import { useIntl } from 'umi';

import {
  checkFormState,
  hiddenFieldState,
  hiddenFieldStateAddOrEdit,
} from '@/utils/core/business-logic';
import { splitHeroDetailFields } from '@/components/HeroDetails';
import {
  assignFieldValue,
  CONFIG_FORM_VIEW,
  fieldErrorMessage,
  getConfigFormHeroSummary,
  getDetails,
  setSwitchValue,
  withDefaultFormConfig,
} from '@/utils/infrastructure';

/**
 * Maps DVA state for a configuration form drawer connected component.
 */
export function mapConfigFormState(namespace) {
  return ({ [namespace]: modelState, global, loading }) => {
    // Prefer global (showNotificationsForm) then the namespace model
    // (`errors` / `setValidationErrors` from createCrudModel / utils).
    const globalErrors = global.validationErrors;
    const modelErrors = modelState?.validationErrors;
    const validationErrors =
      (Array.isArray(globalErrors) && globalErrors.length && globalErrors) ||
      (Array.isArray(modelErrors) && modelErrors.length && modelErrors) ||
      [];

    return {
      ...modelState,
      global,
      loadingItem: loading.effects[`${namespace}/fetchItem`],
      loadingSubmit:
        loading.effects[`${namespace}/fetchUpdate`] || loading.effects[`${namespace}/fetchCreate`],
      validationErrors,
      notificationForm: global.notificationForm?.visible,
    };
  };
}

/**
 * Generic form orchestration for Configuration module drawers (add / edit / details).
 */
export function useConfigForm({
  namespace,
  formConfig: baseFormConfig,
  resourceKey,
  showForm,
  item,
  global,
  id,
  detail,
  success,
  validationErrors,
  loadingItem,
  loadingSubmit,
  notificationForm,
  /** Optional: map getDetails rows (e.g. resolve remote labels). */
  transformDetails,
  /** Extra deps that re-run details mapping when transformDetails needs fresh data. */
  transformDetailsDeps = [],
  /**
   * Optional: transform fields after assignFieldValue (edit prefill, dependent params).
   * Signature: (fields, { item, id, global }) => fields
   */
  prepareFields,
}) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { resetFields, setFields, setFieldsValue, getFieldsError, getFieldsValue } = form;
  const hasSiblingFields = useMemo(
    () => (baseFormConfig?.fields || []).some((field) => field.sibling),
    [baseFormConfig],
  );
  const transformDetailsRef = useRef(transformDetails);
  transformDetailsRef.current = transformDetails;
  const prepareFieldsRef = useRef(prepareFields);
  prepareFieldsRef.current = prepareFields;

  const [title, setTitle] = useState('add');
  const [valid, setValid] = useState(false);
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [openDrawer, setOpenDrawer] = useState(!!showForm);
  const [formConfig, setFormConfig] = useState(() => withDefaultFormConfig(baseFormConfig));
  const [isEdit, setIsEdit] = useState(false);
  const [details, setDetails] = useState([]);

  const formConfigRef = useRef(formConfig);
  const notificationFormRef = useRef(notificationForm);
  const validRef = useRef(valid);
  const globalRef = useRef(global);
  const detailFetchPendingRef = useRef(false);
  const detailFetchWasLoadingRef = useRef(false);
  const formFetchPendingRef = useRef(false);
  const formFetchWasLoadingRef = useRef(false);
  const loadDataRef = useRef(() => {});
  /** Last seen values — used so parent→child reset only runs when the parent value really changes (not on submit validate). */
  const prevFieldValuesRef = useRef({});
  const handlerFieldsChangeRef = useRef(null);

  formConfigRef.current = formConfig;
  notificationFormRef.current = notificationForm;
  validRef.current = valid;
  globalRef.current = global;

  const bodyFields = useMemo(() => formConfig.fields.filter((field) => !field.hidden), [
    formConfig.fields,
  ]);

  const hiddenFields = useMemo(() => formConfig.fields.filter((field) => field.hidden), [
    formConfig.fields,
  ]);

  const drawerTitle = useMemo(() => formatMessage({ id: `global.text.${title}` }), [
    title,
    formatMessage,
  ]);

  const drawerSummary = useMemo(() => {
    const mode = detail
      ? CONFIG_FORM_VIEW.DETAILS
      : title === 'edit'
      ? CONFIG_FORM_VIEW.EDIT
      : CONFIG_FORM_VIEW.ADD;
    return getConfigFormHeroSummary(formatMessage, { mode, resourceKey });
  }, [detail, title, formatMessage, resourceKey]);

  useEffect(() => {
    if (!validationErrors?.length) return undefined;
    const timer = setTimeout(() => {
      setFields(fieldErrorMessage(validationErrors));
      // Bring the first errored field into view inside the scrollable body.
      const first = validationErrors[0]?.field;
      if (first) {
        const el =
          document.getElementById(first) ||
          document.querySelector(`[id$="_${first}"]`) ||
          document.querySelector(`.ant-form-item-has-error`);
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [validationErrors, setFields]);

  const assignSwitchValue = useCallback(
    (fields) => {
      setFieldsValue(setSwitchValue(fields));
      // Snapshot after bind so the first validate/submit cycle does not look like a parent change.
      prevFieldValuesRef.current = { ...form.getFieldsValue(true) };
    },
    [setFieldsValue, form],
  );

  const resetFormFields = useCallback(
    (delay = 0) => {
      if (delay > 0) setTimeout(() => resetFields(), delay);
      else resetFields();
    },
    [resetFields],
  );

  const buildDetails = useCallback(
    (entityItem) => {
      const globalState = globalRef.current;
      if (_.isNil(entityItem)) return [];
      const rows = getDetails(baseFormConfig, globalState, entityItem);
      const mapDetails = transformDetailsRef.current;
      return mapDetails
        ? mapDetails(rows, { item: entityItem, global: globalState, formatMessage })
        : rows;
    },
    [baseFormConfig, formatMessage],
  );

  const assignField = useCallback(() => {
    const globalState = globalRef.current;
    if (!detail) {
      let fields = assignFieldValue(baseFormConfig, globalState, item);
      const prepare = prepareFieldsRef.current;
      if (prepare) {
        fields = prepare(fields, { item, id, global: globalState }) || fields;
      }
      const edit = !_.isNil(id);
      const applyFields = (nextFields) => {
        let resolved = nextFields;
        if (hasSiblingFields) {
          const values = setSwitchValue(resolved);
          resolved = hiddenFieldStateAddOrEdit(resolved, values, edit);
        }
        assignSwitchValue(resolved);
        setFormConfig(withDefaultFormConfig({ ...baseFormConfig, fields: resolved }));
      };
      if (_.isNil(id)) {
        applyFields(fields);
      } else {
        resetFields();
        applyFields(fields);
      }
      setTitle(edit ? 'edit' : 'add');
      setIsEdit(edit);
      setValid(false);
    } else {
      setDetails(buildDetails(item));
      setTitle('details');
    }
  }, [
    detail,
    item,
    id,
    baseFormConfig,
    assignSwitchValue,
    resetFields,
    hasSiblingFields,
    buildDetails,
  ]);

  const loadData = useCallback(() => {
    if (!id) {
      assignField();
      setIsInitLoading(false);
      return;
    }
    if (!detail && item && !_.isEmpty(item)) {
      assignField();
      setIsInitLoading(false);
      return;
    }
    if (detail) {
      detailFetchPendingRef.current = true;
      setIsInitLoading(true);
    } else if (!item || _.isEmpty(item)) {
      formFetchPendingRef.current = true;
      setIsInitLoading(true);
    }
    dispatch({
      type: `${namespace}/fetchItem`,
      payload: { id, detail },
    });
  }, [id, detail, item, assignField, dispatch, namespace]);

  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  const onClose = useCallback(() => {
    setOpenDrawer(false);
    setTimeout(() => {
      dispatch({
        type: `${namespace}/showForm`,
        payload: { state: false, item: null },
      });
    }, 100);
  }, [dispatch, namespace]);

  const handlerFieldsChange = useMemo(
    () =>
      _.debounce((changedFields = []) => {
        const curr = changedFields.length
          ? _.head(changedFields)
          : _.head(_.filter(form.getFieldsError(), { touched: true }));
        const attrName = _.head(curr?.name);

        let nextConfig = formConfigRef.current;
        if (hasSiblingFields && attrName) {
          const fieldConfig = _.find(nextConfig.fields, { name: attrName });
          if (fieldConfig?.sibling) {
            const fields = hiddenFieldState({
              attrName,
              attrSibling: fieldConfig.sibling,
              config: nextConfig,
              configDefault: baseFormConfig,
              setFields,
              values: getFieldsValue(),
            });
            nextConfig = withDefaultFormConfig({ ...nextConfig, fields });
            setFormConfig(nextConfig);
            formConfigRef.current = nextConfig;
          }
        }

        if (attrName) {
          // Transitive dependents (parent → child → grandchild) so cascading selects reset fully.
          // Only when the parent value actually changed — submit/validate also fires onFieldsChange
          // and must not wipe dependent fields (e.g. entityValue / campo meta while saving).
          const currentValue = form.getFieldValue(attrName);
          const previousValue = prevFieldValuesRef.current[attrName];
          const parentValueChanged = !_.isEqual(previousValue, currentValue);
          prevFieldValuesRef.current[attrName] = currentValue;

          if (parentValueChanged) {
            const collectDescendants = (parentName) => {
              const direct = (nextConfig.fields || [])
                .filter((field) => field.parent === parentName)
                .map((field) => field.name);
              return direct.reduce((acc, name) => [...acc, name, ...collectDescendants(name)], []);
            };
            const childFields = collectDescendants(attrName);
            if (childFields.length) {
              resetFields(childFields);
              childFields.forEach((name) => {
                delete prevFieldValuesRef.current[name];
              });
              dispatch({ type: 'global/resetApiData', payload: childFields });
            }
          }
        }

        const errors = getFieldsError();
        const getValid = checkFormState(nextConfig, form);
        if (notificationFormRef.current) dispatch({ type: 'global/hideNotificationForm' });
        if (validRef.current !== getValid || notificationFormRef.current) {
          setValid(getValid);
          setFields(errors);
        } else {
          setValid(getValid);
        }
      }, 500),
    [
      dispatch,
      form,
      getFieldsError,
      setFields,
      hasSiblingFields,
      baseFormConfig,
      getFieldsValue,
      resetFields,
    ],
  );

  useEffect(() => () => handlerFieldsChange.cancel(), [handlerFieldsChange]);

  handlerFieldsChangeRef.current = handlerFieldsChange;

  useEffect(() => {
    setOpenDrawer(showForm);
    if (showForm) loadDataRef.current();
    return () => {
      handlerFieldsChangeRef.current?.cancel();
      resetFormFields(500);
      dispatch({ type: 'global/clearValidationForm' });
      setValid(false);
      setIsInitLoading(true);
      detailFetchPendingRef.current = false;
      detailFetchWasLoadingRef.current = false;
      formFetchPendingRef.current = false;
      formFetchWasLoadingRef.current = false;
      prevFieldValuesRef.current = {};
    };
  }, [showForm, resetFormFields, dispatch]);

  // Re-bind field values only when entity context changes — not on global.apiData
  // updates from remote selects (would reset the form and re-fetch in a loop).
  useEffect(() => {
    assignField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, detail, id]);

  // Keep form field list in sync when schema is built async (e.g. users extraFields).
  useEffect(() => {
    if (!showForm || detail || !_.isNil(id)) return;
    assignField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFormConfig]);

  // Re-map details when optional deps change (e.g. remote labels for detail view).
  useEffect(() => {
    if (!detail || !item || _.isEmpty(item)) return;
    setDetails(buildDetails(item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, transformDetailsDeps);

  useEffect(() => {
    if (!showForm) return;
    if (!id) {
      setIsInitLoading(false);
      return;
    }
    if (detail && detailFetchPendingRef.current) {
      if (loadingItem) {
        detailFetchWasLoadingRef.current = true;
        setIsInitLoading(true);
      } else if (detailFetchWasLoadingRef.current) {
        setIsInitLoading(false);
        detailFetchPendingRef.current = false;
        detailFetchWasLoadingRef.current = false;
      }
      return;
    }
    if (!detail && formFetchPendingRef.current) {
      if (loadingItem) {
        formFetchWasLoadingRef.current = true;
        setIsInitLoading(true);
      } else if (formFetchWasLoadingRef.current) {
        setIsInitLoading(false);
        formFetchPendingRef.current = false;
        formFetchWasLoadingRef.current = false;
      }
      return;
    }
    if (!detail && item && !_.isEmpty(item) && !loadingItem) {
      setIsInitLoading(false);
    }
  }, [showForm, id, item, loadingItem, detail]);

  useEffect(() => {
    if (success) onClose();
  }, [success, onClose]);

  const finishHandler = useCallback(
    (payload) => {
      if (!validRef.current) return;
      if (isEdit) {
        dispatch({
          type: `${namespace}/fetchUpdate`,
          payload: { data: { ...payload, id } },
        });
      } else {
        dispatch({
          type: `${namespace}/fetchCreate`,
          payload: { data: payload },
        });
      }
    },
    [isEdit, dispatch, namespace, id],
  );

  const deleteItemConfirm = useCallback(() => {
    const itemId = item?.id;
    if (!itemId) return;
    dispatch({
      type: 'global/modalConfirm',
      payload: { state: true, id: itemId, modelDelete: namespace },
    });
  }, [dispatch, item, namespace]);

  const editHandler = useCallback(() => {
    dispatch({
      type: `${namespace}/showForm`,
      payload: { state: true, id, item, detail: false },
    });
  }, [dispatch, namespace, id, item]);

  const { mainDetailFields, auditDetailFields } = useMemo(() => {
    const { mainFields, auditFields } = splitHeroDetailFields(details);
    return { mainDetailFields: mainFields, auditDetailFields: auditFields };
  }, [details]);

  const awaitingEditItemFetch = Boolean(id) && !detail && (_.isNil(item) || _.isEmpty(item));
  const isDetailsLoading = openDrawer && detail && isInitLoading;
  /**
   * Edit skeleton shows while fetchItem is in flight. Requires an empty item — list handlers
   * with partial `select` payloads must pass `null` as item on onEdit (see power-bi-server utils).
   */
  const showEditFormSkeleton = openDrawer && awaitingEditItemFetch && isInitLoading;
  const showFormBody = !detail && !showEditFormSkeleton && (_.isNil(id) || !_.isEmpty(item));
  const showFormFooter = showFormBody || showEditFormSkeleton;
  const isPrimaryDisabled = !valid || showEditFormSkeleton;

  const detailsActions = useMemo(
    () =>
      detail
        ? {
            onEdit: editHandler,
            onDelete: deleteItemConfirm,
          }
        : undefined,
    [detail, editHandler, deleteItemConfirm],
  );

  return {
    form,
    formConfig,
    setFormConfig,
    openDrawer,
    onClose,
    drawerTitle,
    drawerSummary,
    bodyFields,
    hiddenFields,
    valid,
    isEdit,
    isDetailsLoading,
    showFormBody,
    showFormFooter,
    showEditFormSkeleton,
    isPrimaryDisabled,
    mainDetailFields,
    auditDetailFields,
    detailsActions,
    finishHandler,
    handlerFieldsChange,
    isSubmitting: loadingSubmit,
  };
}

export default useConfigForm;
